import React, { FC } from 'react';
import {
  Box,
  FormControlLabel,
  Radio,
  RadioGroup,
  SxProps,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { getMatrixColumnLabel, getMatrixRowLabel } from '../../shared/helpers/SurveyMatrix';

interface Props {
  question: any; // same loose typing as the sibling question components
  selections: Record<string, number>; // rowId -> 0-based column index
  onSelect: (rowId: string, columnIndex: number) => void;
  onClear?: (rowId: string) => void; // only for an optional matrix: clicking the selected column clears it
  selectedLanguage: string;
}

// A grid, not a <table>: a table cannot size one column to its content up to a cap while the others share
// the rest equally. The statement column takes its text's width up to 30%; the answer columns split what is
// left evenly and never shrink below ANSWER_COLUMN_MIN_WIDTH, so labels wrap between words instead of being
// hyphenated. With too many columns for that, the wrapper scrolls sideways.
const STATEMENT_COLUMN_MAX_WIDTH = '30%';
const ANSWER_COLUMN_MIN_WIDTH = '6rem';

const CustomQuestionMatrix: FC<Props> = ({
  question,
  selections,
  onSelect,
  onClear,
  selectedLanguage,
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const rows: any[] = question.rows ?? [];
  const options: any[] = question.options ?? [];
  const gridTemplateColumns = `fit-content(${STATEMENT_COLUMN_MAX_WIDTH}) repeat(${options.length}, minmax(${ANSWER_COLUMN_MIN_WIDTH}, 1fr))`;
  const cellSx: SxProps<Theme> = {
    ...theme.typography.body2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding: theme.spacing(0.75, 1),
    borderBottom: `1px solid ${theme.palette.divider}`,
  };

  // A checked radio fires no change event, so clearing hangs off the click instead.
  const clearIfSelected = (rowId: string, columnIndex: number) => {
    if (onClear && selections[rowId] === columnIndex) onClear(rowId);
  };

  if (isSmallScreen) {
    return (
      <Box className="survey-custom-question survey-custom-question--matrix survey-matrix--stacked">
        {rows.map((row, rowIndex) => (
          <Box
            key={row.id}
            className="survey-matrix-row survey-matrix-block"
            sx={{ mb: theme.spacing(3) }}
          >
            <Typography
              id={`${question.questionId}-${row.id}-statement`}
              className="survey-matrix-statement"
              sx={{ fontWeight: 'bold', mb: theme.spacing(1) }}
            >
              {getMatrixRowLabel(question, rowIndex, selectedLanguage)}
            </Typography>
            <RadioGroup
              aria-labelledby={`${question.questionId}-${row.id}-statement`}
              className="survey-answer-options survey-answer-options--matrix"
              name={`${question.questionId}-${row.id}`}
              value={selections[row.id] !== undefined ? String(selections[row.id]) : ''}
              onChange={(event) => onSelect(row.id, Number(event.target.value))}
            >
              {options.map((_option, columnIndex) => (
                <FormControlLabel
                  key={columnIndex}
                  className="survey-answer-option survey-matrix-cell"
                  value={String(columnIndex)}
                  control={<Radio onClick={() => clearIfSelected(row.id, columnIndex)} />}
                  label={getMatrixColumnLabel(question, columnIndex, selectedLanguage)}
                />
              ))}
            </RadioGroup>
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <Box className="survey-custom-question survey-custom-question--matrix">
      <Box
        className="survey-matrix-table-wrapper"
        sx={{ overflowX: 'auto' }}
      >
        <Box
          role="table"
          className="survey-matrix-table"
          sx={{ display: 'grid', gridTemplateColumns }}
        >
          <Box
            role="row"
            className="survey-matrix-header-row"
            sx={{ display: 'contents' }}
          >
            <Box
              role="columnheader"
              className="survey-matrix-corner"
              sx={cellSx}
            />
            {options.map((_option, columnIndex) => (
              <Box
                key={columnIndex}
                role="columnheader"
                className="survey-matrix-column-label"
                sx={{
                  ...cellSx,
                  alignItems: 'flex-end',
                  hyphens: 'manual',
                  overflowWrap: 'break-word',
                }}
              >
                {getMatrixColumnLabel(question, columnIndex, selectedLanguage)}
              </Box>
            ))}
          </Box>
          {rows.map((row, rowIndex) => {
            const rowLabel = getMatrixRowLabel(question, rowIndex, selectedLanguage);
            return (
              <Box
                key={row.id}
                role="row"
                className="survey-matrix-row"
                sx={{ display: 'contents' }}
              >
                <Box
                  role="rowheader"
                  className="survey-matrix-statement"
                  sx={{
                    ...cellSx,
                    justifyContent: 'flex-start',
                    textAlign: 'left',
                    overflowWrap: 'break-word',
                  }}
                >
                  {rowLabel}
                </Box>
                {options.map((_option, columnIndex) => {
                  const columnLabel = getMatrixColumnLabel(question, columnIndex, selectedLanguage);
                  return (
                    <Box
                      key={columnIndex}
                      role="cell"
                      className="survey-matrix-cell"
                      sx={cellSx}
                    >
                      <Radio
                        checked={selections[row.id] === columnIndex}
                        onChange={() => onSelect(row.id, columnIndex)}
                        onClick={() => clearIfSelected(row.id, columnIndex)}
                        name={`${question.questionId}-${row.id}`}
                        value={columnIndex}
                        inputProps={{ 'aria-label': `${rowLabel}: ${columnLabel}` }}
                      />
                    </Box>
                  );
                })}
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default CustomQuestionMatrix;
